import React, { useEffect, useRef, useState } from 'react';
import { Alert, CircularProgress, IconButton, Snackbar, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MspAnalyticsApi from '../apis/mspAnalyticsApi';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import { ticketSpreadsheetClipboard, ticketSpreadsheetHeaders } from '../utils/ticketSpreadsheetClipboard';

interface Props {
  days: number; platformId?: number; group: {by: string; key: string}; customerIds?: string[]; queueIds?: string[];
  ticketId?: number; ticketNumber?: string; disabled?: boolean; headersOnly?: boolean;
}

export default function TicketAnalysisCopyButton({days,platformId,group,customerIds=[],queueIds=[],ticketId,ticketNumber,disabled,headersOnly=false}: Props) {
  const [busy,setBusy]=useState(false), [count,setCount]=useState(0);
  const [message,setMessage]=useState<{text:string;error:boolean}|null>(null);
  const pending=useRef<AbortController>();
  const scope=JSON.stringify([days,platformId,group.by,group.key,customerIds,queueIds,ticketId,headersOnly]);
  useEffect(()=>{setBusy(false);setMessage(null);return()=>{pending.current?.abort();pending.current=undefined;};},[scope]);
  const label=headersOnly?'Copy headers':ticketId==null?'Copy all tickets':`Copy ticket ${ticketNumber || ticketId}`;
  const copy=async()=>{
    if((!headersOnly && !platformId) || busy)return;
    if(!navigator.clipboard){setMessage({text:'Clipboard access is unavailable in this browser.',error:true});return;}
    const controller=new AbortController();pending.current=controller;
    setBusy(true);setCount(0);setMessage(null);
    let copied=0;
    const content=headersOnly?Promise.resolve(ticketSpreadsheetHeaders()):MspAnalyticsApi.exportTicketCsv({days,platform_id:platformId!,group:{by:group.by,key:group.key},
      customer_ids:customerIds.join(','),queue_ids:queueIds.join(','),ticket_id:ticketId,origin:window.location.origin},controller.signal,
    value=>{copied=value;if(!controller.signal.aborted)setCount(value);}).then(async blob=>{
      const text=await blob.text();
      if(controller.signal.aborted)throw new DOMException('Ticket copy cancelled','AbortError');
      return ticketSpreadsheetClipboard(text);
    });
    const representations:Promise<Blob>[]=[];
    // ClipboardItem accepts a promise so Safari retains the initiating click's activation.
    try {
      if(typeof ClipboardItem!=='undefined' && navigator.clipboard.write) {
        const html=content.then(value=>new Blob([value.html],{type:'text/html'}));
        const plain=content.then(value=>new Blob([value.plainText],{type:'text/plain'}));
        representations.push(html,plain);
        await navigator.clipboard.write([new ClipboardItem({'text/html':html,'text/plain':plain})]);
      } else {
        await navigator.clipboard.writeText((await content).plainText);
      }
      if(!controller.signal.aborted)setMessage({text:headersOnly?'Copied headers.':`Copied ${copied} ticket${copied===1?'':'s'}.`,error:false});
    } catch(error) {
      if(!controller.signal.aborted)setMessage({text:getApiErrorMessage(error,'Unable to copy tickets. Please try again.'),error:true});
      controller.abort();
      await Promise.allSettled([content,...representations]);
    } finally {if(pending.current===controller){pending.current=undefined;setBusy(false);}}
  };
  return <>
    <Tooltip title={busy?`Preparing copy · ${count.toLocaleString()} tickets`:label}><span>
      <IconButton size="small" color="primary" aria-label={label} disabled={disabled || (!headersOnly && !platformId) || busy} onClick={()=>void copy()}>
        {busy?<CircularProgress size={16}/>:<ContentCopyIcon fontSize="small"/>}
      </IconButton>
    </span></Tooltip>
    <Snackbar open={!!message} autoHideDuration={5000} onClose={()=>setMessage(null)}>
      <Alert severity={message?.error?'error':'success'} onClose={()=>setMessage(null)}>{message?.text}</Alert>
    </Snackbar>
  </>;
}
